/**
 * @fileoverview Zustand store for managing saved bookings with localStorage persistence.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Saved booking info stored locally
 */
export interface SavedBooking {
  id: number;
  managementToken: string;
  customerName: string;
  customerEmail: string;
  bookingDate: string;
  bookingTime: string;
  numGuests: number;
  tableName: string;
  status: 'confirmed' | 'cancelled' | 'completed';
  createdAt: string;
}

interface BookingStore {
  /** List of saved bookings */
  bookings: SavedBooking[];

  /** Add a new booking to saved list */
  addBooking: (booking: SavedBooking) => void;

  /** Remove a booking by ID */
  removeBooking: (id: number) => void;

  /** Update booking status */
  updateBookingStatus: (id: number, status: SavedBooking['status']) => void;

  /** Clear all saved bookings */
  clearAllBookings: () => void;

  /** Get upcoming bookings (not cancelled, date >= today) */
  getUpcomingBookings: () => SavedBooking[];

  /** Get past bookings */
  getPastBookings: () => SavedBooking[];
}

/**
 * Booking store with localStorage persistence.
 * Automatically saves and loads bookings from localStorage.
 */
export const useBookingStore = create<BookingStore>()(
  persist(
    (set, get) => ({
      bookings: [],

      addBooking: (booking) => {
        set((state) => {
          // Check if booking already exists (avoid duplicates)
          const exists = state.bookings.some((b) => b.id === booking.id);
          if (exists) {
            // Update existing booking
            return {
              bookings: state.bookings.map((b) =>
                b.id === booking.id ? { ...b, ...booking } : b
              ),
            };
          }
          // Add new booking at the beginning
          return { bookings: [booking, ...state.bookings] };
        });
      },

      removeBooking: (id) => {
        set((state) => ({
          bookings: state.bookings.filter((b) => b.id !== id),
        }));
      },

      updateBookingStatus: (id, status) => {
        set((state) => ({
          bookings: state.bookings.map((b) =>
            b.id === id ? { ...b, status } : b
          ),
        }));
      },

      clearAllBookings: () => {
        set({ bookings: [] });
      },

      getUpcomingBookings: () => {
        const today = new Date().toISOString().split('T')[0];
        return get().bookings.filter(
          (b) => b.status === 'confirmed' && b.bookingDate >= today
        );
      },

      getPastBookings: () => {
        const today = new Date().toISOString().split('T')[0];
        return get().bookings.filter(
          (b) => b.status !== 'confirmed' || b.bookingDate < today
        );
      },
    }),
    {
      name: 'booking-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ bookings: state.bookings }),
    }
  )
);

/**
 * Hook to get the count of upcoming bookings (for badge)
 */
export function useUpcomingBookingCount(): number {
  return useBookingStore((state) => {
    const today = new Date().toISOString().split('T')[0];
    return state.bookings.filter(
      (b) => b.status === 'confirmed' && b.bookingDate >= today
    ).length;
  });
}
